import React, { useState } from "react";
import toast from "react-hot-toast";
import { FiCheck, FiInfo, FiMapPin, FiUserPlus, FiX } from "react-icons/fi";
import { TbHomeMove } from "react-icons/tb";
import client from "../api/client";
import CustomModal from "./CustomModal";
import AddContactModal from "./AddContactModal";
import ButtonLoader from "./ButtonLoader";
import { showApiError } from "../utils";
import { formatCityStateZip } from "../utils/address";

const Steps = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs font-medium">
    {["Confirm", "Add Contacts"].map((label, index) => {
      const active = step === index + 1;
      const done = step > index + 1;
      return (
        <li key={label} className="flex items-center gap-2">
          <span
            className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
              active || done ? "bg-canvas-primary text-white" : "bg-gray-200 text-slate-700"
            }`}
          >
            {done ? <FiCheck /> : index + 1}
          </span>
          <span className={active || done ? "text-canvas-primary" : "text-slate-600"}>{label}</span>
          {index === 0 && <span className="h-px w-6 bg-gray-300" />}
        </li>
      );
    })}
  </ol>
);

const MovedHouseholdModal = ({ household, open, onClose, onMoved }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [newHousehold, setNewHousehold] = useState(null);
  const [addContactOpen, setAddContactOpen] = useState(false);

  const props = household?.properties || {};
  const addressLine = props.residence_address_line || props.aristotle_address || "--";
  const city = props.residence_city || props.aristotle_city || props.mailing_city || "";
  const state = props.residence_state || "";
  const zip = props.residence_zip || props.aristotle_zip;
  const cityLine = formatCityStateZip({ city, state, zip });

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      const { data } = await client.post("/household/moved", { householdId: household.id });
      setNewHousehold(data.newHousehold);
      onMoved?.(household.id, data.newHousehold);
      toast.success("New household created");
    } catch (error) {
      const created = error?.response?.data?.newHousehold;
      if (created) {
        setNewHousehold(created);
        onMoved?.(household.id, created);
      }
      showApiError(error, "Could not mark household as Moved");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (isSaving) return;
    setNewHousehold(null);
    setAddContactOpen(false);
    onClose();
  };

  const step = newHousehold ? 2 : 1;

  return (
    <>
      <CustomModal open={open} onClose={handleClose} panelClass="max-w-lg w-full">
        <div className="flex flex-col gap-5 text-canvas-primary">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-canvas-primary">
                <TbHomeMove className="h-5 w-5" />
              </span>
              <div>
                <h2 className="text-lg font-semibold leading-tight">Mark Household as Moved</h2>
                <p className="text-xs text-slate-600">Set up the new household for the people who moved</p>
              </div>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={handleClose}
              disabled={isSaving}
              className="rounded-full p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>

          <Steps step={step} />

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              <FiMapPin />
              {newHousehold ? "New household address" : "Address copied to the new household"}
            </div>
            <div className="text-base font-semibold">{addressLine}</div>
            <div className="text-sm">{cityLine}</div>
          </div>

          {!newHousehold ? (
            <>
              <ul className="flex flex-col gap-2 text-sm">
                <li className="flex gap-2">
                  <FiInfo className="mt-0.5 shrink-0 text-slate-600" />
                  <span>A new household is created with this address only. Nothing else is copied.</span>
                </li>
                <li className="flex gap-2">
                  <FiInfo className="mt-0.5 shrink-0 text-slate-600" />
                  <span>The original household keeps its contacts and gets a Moved badge. You can undo this at any time.</span>
                </li>
              </ul>
              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium disabled:opacity-50"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-lg bg-canvas-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  onClick={handleConfirm}
                  disabled={isSaving}
                >
                  {isSaving ? <ButtonLoader /> : "Create and Add Contacts"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
                <FiCheck className="shrink-0" />
                New household created. Add the contacts who moved.
              </div>
              <div className="flex justify-end gap-3 border-t pt-4">
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium"
                  onClick={handleClose}
                >
                  Done
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 rounded-lg bg-canvas-primary px-4 py-2 text-sm font-semibold text-white"
                  onClick={() => setAddContactOpen(true)}
                >
                  <FiUserPlus />
                  Add Contact
                </button>
              </div>
            </>
          )}
        </div>
      </CustomModal>
      {newHousehold && (
        <AddContactModal
          isOpen={addContactOpen}
          onRequestClose={() => setAddContactOpen(false)}
          data={{
            l2Id: newHousehold.properties?.l2_household_id,
            residence_state: props.residence_state,
            residence_zip: props.residence_zip,
          }}
        />
      )}
    </>
  );
};

export default MovedHouseholdModal;
